import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Pencil, Trash2, UserCircle2, Loader2, Mail,
  Clock, XCircle, RefreshCw, Copy, CheckCheck,
} from 'lucide-react'
import { toast } from 'sonner'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@meno/ui/components/ui/card'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Label } from '@meno/ui/components/ui/label'
import { Badge } from '@meno/ui/components/ui/badge'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import { PhoneInput, isValidPhone } from '@meno/ui/components/ui/PhoneInput'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@meno/ui/components/ui/table'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@meno/ui/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@meno/ui/components/ui/select'
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@meno/ui/components/ui/tabs'
import { getBranches, sendInvite, listInvites, revokeInvite } from '@meno/ui/lib/api'
import api from '@meno/ui/lib/api'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { TextInput } from '@meno/ui/components/shared/TextInput'
import { FrameMark } from '@meno/ui/components/FrameMark'
import { BranchMultiSelect } from '@meno/ui/components/shared/BranchMultiSelect'

// Pragmatic email shape (local@domain.tld) — mirrors the backend check.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Branch = { id: number; name: string }
type User = {
  id: number; name: string; email: string; phone?: string
  role: string; is_active: boolean; branches: Branch[]
}
type Invite = {
  id: number; email: string; name?: string; role: string
  invited_by_name: string; status: string
  invite_url?: string
  app_scope?: string[]; branch_ids?: number[]
}

const ROLES = ['admin', 'manager', 'staff']
const EMPTY = { name: '', email: '', phone: '', role: 'staff', is_active: true, branch_ids: [] as number[] }

export default function UsersPage() {
  const { t } = useTranslation()
  usePageHeader({ title: t('nav.users', 'Users') })

  // Users
  const [users,     setUsers]     = useState<User[]>([])
  const [branches,  setBranches]  = useState<Branch[]>([])
  const [open,      setOpen]      = useState(false)
  const [editing,   setEditing]   = useState<User | null>(null)
  const [form,      setForm]      = useState({ ...EMPTY })
  const [search,    setSearch]    = useState('')
  const [deleteId,  setDeleteId]  = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving,  setIsSaving]  = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  // Invites
  const [invites,       setInvites]       = useState<Invite[]>([])
  const [inviteOpen,    setInviteOpen]    = useState(false)
  const [inviteForm,    setInviteForm]    = useState({ email: '', name: '', role: 'staff', branch_ids: [] as number[] })
  const [isSendingInv,  setIsSendingInv]  = useState(false)
  const [revokeId,      setRevokeId]      = useState<number | null>(null)
  const [devUrl,        setDevUrl]        = useState<string | null>(null)
  const [copied,        setCopied]        = useState(false)
  const [resendingId,   setResendingId]   = useState<number | null>(null)

  const fetchAll = async () => {
    const [u, b, inv] = await Promise.all([
      api.get('/users'),
      getBranches(),
      listInvites(),
    ])
    setUsers(u.data)
    setBranches(b.data)
    setInvites(inv.data)
  }

  const load = async () => {
    setIsLoading(true)
    try {
      await fetchAll()
    } finally {
      setIsLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await fetchAll()
      toast.success(t('common.refreshed', 'Refreshed'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? t('common.refreshError', 'Could not refresh'))
    } finally {
      setRefreshing(false)
    }
  }

  // ── User CRUD ──────────────────────────────────────────────────────────────
  // Members are added via invite only — direct creation used to hand back a
  // one-time magic-login link with no email and no way to retrieve it again
  // if the admin lost the dialog. This dialog is edit-only now.
  const openEdit = (u: User) => {
    setEditing(u)
    setForm({ name: u.name, email: u.email, phone: u.phone ?? '', role: u.role, is_active: u.is_active, branch_ids: u.branches.map(b => b.id) })
    setOpen(true)
  }

  const save = async () => {
    if (!editing || !isValidPhone(form.phone)) return
    const payload = { name: form.name, email: form.email, phone: form.phone || undefined, role: form.role, is_active: form.is_active, branch_ids: form.branch_ids }
    setIsSaving(true)
    try {
      const { data } = await api.patch(`/users/${editing.id}`, payload)
      setUsers(prev => prev.map(u => u.id === editing.id ? data : u))
      setOpen(false)
    } finally {
      setIsSaving(false)
    }
  }

  const remove = async () => {
    if (deleteId === null) return
    await api.delete(`/users/${deleteId}`)
    setUsers(prev => prev.filter(u => u.id !== deleteId))
    setDeleteId(null)
  }

  // ── Invite actions ─────────────────────────────────────────────────────────
  // A member must have a name and be assigned to at least one branch — name +
  // email + branch are the hard requirements to send.
  const inviteEmailValid = EMAIL_RE.test(inviteForm.email.trim())
  const inviteNameValid  = inviteForm.name.trim().length > 0
  const inviteFormValid  = inviteEmailValid && inviteNameValid && inviteForm.branch_ids.length > 0

  const handleSendInvite = async () => {
    if (!inviteFormValid) return
    setIsSendingInv(true)
    try {
      const { data } = await sendInvite({ email: inviteForm.email.trim(), name: inviteForm.name.trim(), role: inviteForm.role, branch_ids: inviteForm.branch_ids })
      setInvites(prev => [data, ...prev])
      if (data.invite_url) {
        // Dev mode — SMTP not configured, show the link
        setDevUrl(data.invite_url)
      } else {
        toast.success(t('users.inviteSentTo', 'Invite sent to {{email}}', { email: inviteForm.email }))
        setInviteOpen(false)
      }
      setInviteForm({ email: '', name: '', role: 'staff', branch_ids: [] })
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? t('users.failedToSendInvite', 'Failed to send invite'))
    } finally {
      setIsSendingInv(false)
    }
  }

  const handleResendInvite = async (inv: Invite) => {
    setResendingId(inv.id)
    try {
      const { data } = await sendInvite({
        email: inv.email, name: inv.name ?? '', role: inv.role,
        app_scope: inv.app_scope ?? [], branch_ids: inv.branch_ids ?? [],
      })
      setInvites(prev => [data, ...prev.map(i => i.id === inv.id ? { ...i, status: 'revoked' } : i)])
      if (data.invite_url) {
        // Dev mode — SMTP not configured, show the link
        setDevUrl(data.invite_url)
        setInviteOpen(true)
      } else {
        toast.success(t('users.inviteResentTo', 'Invite resent to {{email}}', { email: inv.email }))
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? t('users.failedToResendInvite', 'Failed to resend invite'))
    } finally {
      setResendingId(null)
    }
  }

  const handleRevoke = async () => {
    if (revokeId === null) return
    await revokeInvite(revokeId)
    setInvites(prev => prev.map(i => i.id === revokeId ? { ...i, status: 'revoked' } : i))
    setRevokeId(null)
    toast.success(t('users.inviteRevoked', 'Invite revoked'))
  }

  const copyUrl = () => {
    if (!devUrl) return
    navigator.clipboard.writeText(devUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filtered = users.filter(u =>
    (u.name || '').toLowerCase().includes((search || '').toLowerCase()) ||
    (u.email || '').toLowerCase().includes((search || '').toLowerCase())
  )
  const pendingInvites = invites.filter(i => i.status === 'pending')

  // Role chips — brand palette only. Admin uses the primary-red accent to
  // call out elevated permissions; manager / staff sit on the neutral
  // muted-grey chip. Role name is the load-bearing signifier; colour
  // distinctions between the lower tiers were noise.
  const roleColor: Record<string, string> = {
    admin:   'bg-muted text-primary border-primary/20',
    manager: 'bg-muted text-foreground border-border',
    staff:   'bg-muted text-muted-foreground border-border',
  }
  const statusBadge = (s: string) => {
    if (s === 'pending')  return <Badge variant="secondary" className="gap-1"><Clock className="w-3 h-3" />{t('users.pending', 'Pending')}</Badge>
    if (s === 'accepted') return <Badge variant="default" className="gap-1 bg-primary text-primary-foreground">{t('users.accepted', 'Accepted')}</Badge>
    return <Badge variant="secondary" className="gap-1 text-muted-foreground">{t('users.revokedStatus', 'Revoked')}</Badge>
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{t('users.title')}</h2>
          <p className="text-muted-foreground text-sm mt-1">{t('users.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-9 w-9 shrink-0"
            title={t('common.refresh', 'Refresh')}
            aria-label={t('common.refresh', 'Refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" className="gap-2" onClick={() => { setInviteOpen(true); setDevUrl(null) }}>
            <Mail className="w-4 h-4" /> {t('users.inviteByEmail', 'Invite by Email')}
          </Button>
        </div>
      </div>

      {/* Full-width chrome strip — same bg as the TopBar (light) / brand-
          black (dark). Negative margins bleed it to the SidebarInset edges
          so it reads as a primary nav bar rather than a pill control. The
          underline trigger style mirrors ScreenDetailsPage / SubscriptionPage. */}
      <Tabs defaultValue="members">
        <div
          className="mb-6 border-b border-border brand-chrome-light"
          style={{ marginInline: 'calc(-1 * var(--space-l))' }}
        >
          <div className="px-fluid-l">
            <TabsList className="h-11 bg-transparent p-0 rounded-none gap-1">
              <TabsTrigger
                value="members"
                className="h-11 rounded-none border-b-2 border-transparent bg-transparent px-4 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                {t('users.tabMembers', 'Members')}
                {!isLoading && <span className="ml-1.5 text-xs opacity-60">({users.length})</span>}
              </TabsTrigger>
              <TabsTrigger
                value="invites"
                className="h-11 rounded-none border-b-2 border-transparent bg-transparent px-4 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                {t('users.tabInvites', 'Invites')}
                {pendingInvites.length > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-primary text-white rounded-full">
                    {pendingInvites.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        {/* ── Members tab ── */}
        <TabsContent value="members" className="mt-4 space-y-4">
          <Input
            placeholder={t('common.search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-xs"
          />

          <Card className="bg-card">
            <CardContent className="p-0">
              <div className="hidden sm:block">
                <Table className="min-w-[640px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common.name')}</TableHead>
                      <TableHead>{t('common.email')}</TableHead>
                      <TableHead>{t('users.role')}</TableHead>
                      <TableHead>{t('branches.title')}</TableHead>
                      <TableHead>{t('common.status')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading
                      ? Array.from({ length: 4 }).map((_, i) => (
                          <TableRow key={i}>
                            <TableCell><div className="flex items-center gap-2"><Skeleton className="h-7 w-7 rounded-full" /><Skeleton className="h-4 w-28" /></div></TableCell>
                            <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-14 rounded-full" /></TableCell>
                            <TableCell><Skeleton className="h-7 w-16 rounded-md" /></TableCell>
                          </TableRow>
                        ))
                      : filtered.length === 0
                      ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">{t('common.noData')}</TableCell></TableRow>
                      : filtered.map(u => (
                          <TableRow key={u.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                                  <UserCircle2 className="w-4 h-4 text-primary" />
                                </div>
                                <span className="font-medium">{u.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{u.email}</TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${roleColor[u.role] ?? roleColor.staff}`}>
                                {t(`users.roles.${u.role}`, u.role)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {u.branches.length === 0
                                  ? <span className="text-muted-foreground text-xs">—</span>
                                  : u.branches.map(b => <Badge key={b.id} variant="secondary" className="text-xs">{b.name}</Badge>)
                                }
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={u.is_active ? 'default' : 'secondary'}>
                                {u.is_active ? t('common.active') : t('common.inactive')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button variant="ghost" size="icon" onClick={() => openEdit(u)}><Pencil className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeleteId(u.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                    }
                  </TableBody>
                </Table>
              </div>

              <div className="sm:hidden divide-y divide-border">
                {isLoading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="p-4 flex items-center gap-2">
                        <Skeleton className="h-7 w-7 rounded-full" />
                        <div className="flex-1 space-y-1.5">
                          <Skeleton className="h-4 w-28" />
                          <Skeleton className="h-3 w-40" />
                        </div>
                      </div>
                    ))
                  : filtered.length === 0
                  ? <p className="text-center text-muted-foreground py-10 text-sm">{t('common.noData')}</p>
                  : filtered.map(u => (
                      <div key={u.id} className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                              <UserCircle2 className="w-4 h-4 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{u.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(u)}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeleteId(u.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${roleColor[u.role] ?? roleColor.staff}`}>
                            {t(`users.roles.${u.role}`, u.role)}
                          </span>
                          <Badge variant={u.is_active ? 'default' : 'secondary'}>
                            {u.is_active ? t('common.active') : t('common.inactive')}
                          </Badge>
                        </div>
                        {u.branches.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {u.branches.map(b => <Badge key={b.id} variant="secondary" className="text-xs">{b.name}</Badge>)}
                          </div>
                        )}
                      </div>
                    ))
                }
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Invites tab ── */}
        <TabsContent value="invites" className="mt-4 space-y-4">
          <Card className="bg-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('users.invitesCardTitle', 'Pending & Past Invitations')}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="hidden sm:block">
                <Table className="min-w-[640px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common.name')}</TableHead>
                      <TableHead>{t('common.email')}</TableHead>
                      <TableHead>{t('users.role')}</TableHead>
                      <TableHead>{t('users.invitedBy', 'Invited By')}</TableHead>
                      <TableHead>{t('common.status')}</TableHead>
                      <TableHead className="text-right">{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading
                      ? Array.from({ length: 3 }).map((_, i) => (
                          <TableRow key={i}>
                            <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                            <TableCell><Skeleton className="h-7 w-20 rounded-md ml-auto" /></TableCell>
                          </TableRow>
                        ))
                      : invites.length === 0
                      ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                              <Mail className="w-8 h-8 mx-auto mb-2 opacity-20" />
                              {t('users.noInvitesYet', 'No invites sent yet. Use "Invite by Email" to add team members.')}
                            </TableCell>
                          </TableRow>
                        )
                      : invites.map(inv => (
                          <TableRow key={inv.id} className={inv.status !== 'pending' ? 'opacity-50' : ''}>
                            <TableCell className="font-medium">{inv.name || <span className="text-muted-foreground">—</span>}</TableCell>
                            <TableCell className="text-muted-foreground">{inv.email}</TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${roleColor[inv.role] ?? roleColor.staff}`}>
                                {t(`users.roles.${inv.role}`, inv.role)}
                              </span>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">{inv.invited_by_name}</TableCell>
                            <TableCell>{statusBadge(inv.status)}</TableCell>
                            <TableCell className="text-right">
                              {inv.status === 'pending' && (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost" size="sm"
                                    className="gap-1.5 h-7 text-xs"
                                    disabled={resendingId === inv.id}
                                    onClick={() => handleResendInvite(inv)}
                                  >
                                    {resendingId === inv.id
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : <RefreshCw className="w-3 h-3" />}
                                    {t('users.resend', 'Resend')}
                                  </Button>
                                  <Button
                                    variant="ghost" size="sm"
                                    className="gap-1.5 h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => setRevokeId(inv.id)}
                                  >
                                    <XCircle className="w-3 h-3" /> {t('users.revoke', 'Revoke')}
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                    }
                  </TableBody>
                </Table>
              </div>

              <div className="sm:hidden divide-y divide-border">
                {isLoading
                  ? Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="p-4 space-y-1.5">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-3 w-40" />
                      </div>
                    ))
                  : invites.length === 0
                  ? (
                      <div className="text-center text-muted-foreground py-10 px-4">
                        <Mail className="w-8 h-8 mx-auto mb-2 opacity-20" />
                        <p className="text-sm">{t('users.noInvitesYet', 'No invites sent yet. Use "Invite by Email" to add team members.')}</p>
                      </div>
                    )
                  : invites.map(inv => (
                      <div key={inv.id} className={`p-4 space-y-2 ${inv.status !== 'pending' ? 'opacity-50' : ''}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{inv.name || <span className="text-muted-foreground">—</span>}</p>
                            <p className="text-xs text-muted-foreground truncate">{inv.email}</p>
                          </div>
                          {statusBadge(inv.status)}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${roleColor[inv.role] ?? roleColor.staff}`}>
                              {t(`users.roles.${inv.role}`, inv.role)}
                            </span>
                            <span className="text-muted-foreground text-xs truncate">{inv.invited_by_name}</span>
                          </div>
                          {inv.status === 'pending' && (
                            <div className="flex gap-1 shrink-0">
                              <Button
                                variant="ghost" size="sm"
                                className="gap-1.5 h-7 text-xs"
                                disabled={resendingId === inv.id}
                                onClick={() => handleResendInvite(inv)}
                              >
                                {resendingId === inv.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <RefreshCw className="w-3 h-3" />}
                                {t('users.resend', 'Resend')}
                              </Button>
                              <Button
                                variant="ghost" size="sm"
                                className="gap-1.5 h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => setRevokeId(inv.id)}
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                }
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Invite dialog ── */}
      <Dialog open={inviteOpen} onOpenChange={open => { setInviteOpen(open); if (!open) setDevUrl(null) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <FrameMark className="w-8 h-8 text-foreground" />
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" /> {t('users.inviteTeamMember', 'Invite Team Member')}
              </DialogTitle>
            </div>
            <DialogDescription>
              {t('users.inviteDialogDesc', 'An invitation email with a secure link (valid 48 hours) will be sent. The recipient can review the details you set here before creating a password.')}
            </DialogDescription>
          </DialogHeader>

          {devUrl ? (
            /* Dev mode — show link when SMTP not configured */
            <div className="space-y-4 py-2">
              <div className="bg-muted/40 border border-border rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-foreground">{t('users.smtpNotConfigured', 'SMTP not configured')}</p>
                <p className="text-xs text-muted-foreground">{t('users.smtpNotConfiguredDesc', 'Email was not sent. Copy this invite link and share it manually:')}</p>
              </div>
              <div className="flex gap-2">
                <Input value={devUrl} readOnly className="text-xs font-mono" />
                <Button variant="outline" size="icon" onClick={copyUrl}>
                  {copied ? <CheckCheck className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <Button className="w-full" onClick={() => { setInviteOpen(false); setDevUrl(null) }}>{t('common.done')}</Button>
            </div>
          ) : (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="inv-name">{t('users.fullName', 'Full name')}</Label>
                <Input
                  id="inv-name"
                  placeholder={t('users.namePlaceholder', 'e.g. Ahmed Al-Rashid')}
                  value={inviteForm.name}
                  onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))}
                  maxLength={150}
                  autoFocus
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="inv-email">{t('users.emailAddress', 'Email address')}</Label>
                <Input
                  id="inv-email"
                  type="email"
                  placeholder={t('users.emailPlaceholder', 'colleague@example.com')}
                  value={inviteForm.email}
                  onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                  className={inviteForm.email.length > 0 && !inviteEmailValid ? 'border-destructive focus-visible:ring-destructive' : ''}
                />
                {inviteForm.email.length > 0 && !inviteEmailValid && (
                  <p className="text-xs text-destructive">{t('users.invalidEmail', 'Enter a valid email address')}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="inv-role">{t('users.role')}</Label>
                <Select value={inviteForm.role} onValueChange={v => setInviteForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger id="inv-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => (
                      <SelectItem key={r} value={r}>
                        <span>{t(`users.roles.${r}`, r)}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {r === 'admin'
                            ? t('users.roleHintAdmin', '— Full access')
                            : r === 'manager'
                            ? t('users.roleHintManager', '— Manage content')
                            : t('users.roleHintStaff', '— View & operate')}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>{t('users.assignBranches')}</Label>
                <BranchMultiSelect
                  branches={branches}
                  selectedIds={inviteForm.branch_ids}
                  onChange={ids => setInviteForm(f => ({ ...f, branch_ids: ids }))}
                />
                {inviteForm.branch_ids.length === 0 && (
                  <p className="text-xs text-destructive">
                    {t('users.branchRequired', 'Select at least one branch')}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>{t('common.cancel')}</Button>
                <Button
                  onClick={handleSendInvite}
                  disabled={!inviteFormValid || isSendingInv}
                  className="gap-2"
                >
                  {isSendingInv && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('users.sendInvite', 'Send Invite')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Edit user dialog ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('users.editUser')}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <TextInput label={t('common.name')} value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} lang="any" maxLength={80} required />
            <div className="grid gap-2">
              <Label>{t('common.email')}</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label>{t('users.phone')}</Label>
              <PhoneInput value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} />
            </div>
            <div className="grid gap-2">
              <Label>{t('users.role')}</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r}>{t(`users.roles.${r}`, r)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>{t('users.assignBranches')}</Label>
              <BranchMultiSelect
                branches={branches}
                selectedIds={form.branch_ids}
                onChange={ids => setForm(f => ({ ...f, branch_ids: ids }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={save} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 animate-spin me-2" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete user confirm ── */}
      <ConfirmDialog
        open={deleteId !== null}
        title={t('common.deleteUser')}
        description={t('common.deleteUserDesc')}
        onConfirm={remove}
        onClose={() => setDeleteId(null)}
      />

      {/* ── Revoke invite confirm ── */}
      <ConfirmDialog
        open={revokeId !== null}
        title={t('users.revokeInviteTitle', 'Revoke Invite')}
        description={t('users.revokeInviteDesc', 'This invite link will stop working immediately. You can send a new invite anytime.')}
        onConfirm={handleRevoke}
        onClose={() => setRevokeId(null)}
      />
    </div>
  )
}
